/*
 * variant-switch.js
 *
 * On this theme, product "variants" (colour, size, viscosity, thickness, …) are
 * separate products linked by URL inside `.product__variants-wrapper`. Clicking
 * one normally triggers a full page reload. This script upgrades that to an
 * in-place AJAX swap: it fetches the target product page and replaces the
 * contents of <main id="MainContent"> with the fetched markup, then updates the
 * URL/title via the History API. Any failure falls back to a normal navigation,
 * so the links keep working without JS or when something goes wrong.
 */
(function () {
  if (window.__variantSwitchInit) return;
  window.__variantSwitchInit = true;

  var MAIN_ID = 'MainContent';
  var VARIANT_LINK_SELECTOR = '.product__variants-wrapper a[href]';
  var SHOW_MORE_SELECTOR = '.product__color-variations-list-show-more';
  var LOADING_CLASS = 'variant-switch--loading';

  var abortController = null;
  var remountCount = 0;

  function getMain() {
    return document.getElementById(MAIN_ID);
  }

  function isModifiedClick(event) {
    return (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  // Swap innerHTML and re-run inline scripts, while skipping external scripts
  // that are already loaded on the page (so custom-element definitions aren't
  // re-fetched and the guarded `customElements.define` calls stay no-ops).
  function swapInnerHTML(target, html) {
    var alreadyLoaded = new Set();
    document.querySelectorAll('script[src]').forEach(function (script) {
      alreadyLoaded.add(script.src);
    });

    target.innerHTML = html;

    target.querySelectorAll('script').forEach(function (oldScript) {
      if (oldScript.src && alreadyLoaded.has(oldScript.src)) {
        oldScript.remove();
        return;
      }
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  // Judge.me's revamped review widget is a Vue app. Its loader (the `judgeme_core`
  // app embed) lives in the layout, outside #MainContent, so it survives the swap
  // and never runs again — while the widget container it mounted was inside the
  // section and has just been replaced by fresh, unmounted markup. Result: an empty
  // review box on every variant switch. Re-executing the widget's entry bundle makes
  // it scan the document and mount whatever it finds.
  //
  // Vue stamps `data-v-app` on mounted roots, so containers lacking it are the ones
  // still waiting. The URL needs a cache-busting param — re-appending a script whose
  // src is already in the module registry is a no-op, so it would never re-run.
  function remountJudgeMeWidgets() {
    var pending = document.querySelectorAll(
      '[data-widget][data-entry-point]:not([data-v-app])'
    );
    if (!pending.length) return;

    var entryPoints = new Set();
    pending.forEach(function (container) {
      entryPoints.add(container.getAttribute('data-entry-point'));
    });

    var loadedScripts = Array.from(document.querySelectorAll('script[src]'));

    entryPoints.forEach(function (entryPoint) {
      var loaded = loadedScripts.filter(function (script) {
        return script.src.indexOf(entryPoint) !== -1;
      })[0];
      // Nothing to re-run: the widget was never loaded on this page to begin with.
      if (!loaded) return;

      var url = new URL(loaded.src);
      url.searchParams.set('variant-switch', String(++remountCount));

      var script = document.createElement('script');
      if (loaded.type) script.type = loaded.type;
      script.async = true;
      script.src = url.href;
      document.head.appendChild(script);
    });
  }

  // Re-hydrate third-party widgets whose markup was just swapped in. Their
  // scripts are already loaded (and deduped on swap), so their globals persist
  // on `window` — we only need to ask them to re-scan the new DOM. Everything is
  // guarded so a missing/failing widget can never break the variant swap.
  function refreshThirdPartyWidgets() {
    // Judge.me preview badges (product cards) — classic, re-rendered in place.
    try {
      if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
        window.jdgm.customizeBadges();
      }
    } catch (error) {
      /* no-op: never let a review widget break the page */
    }

    // Judge.me review widget — revamped, needs a full remount (see above).
    try {
      remountJudgeMeWidgets();
    } catch (error) {
      /* no-op: never let a review widget break the page */
    }
  }

  function navigate(url, options) {
    var push = !options || options.push !== false;
    var main = getMain();
    if (!main) {
      window.location.href = url;
      return;
    }

    // Preserve keyboard focus context so we can restore it after the swap.
    var focusWasInVariants =
      document.activeElement &&
      document.activeElement.closest &&
      !!document.activeElement.closest('.product__variants-wrapper');

    if (abortController) abortController.abort();
    abortController = new AbortController();
    var signal = abortController.signal;

    main.classList.add(LOADING_CLASS);
    main.setAttribute('aria-busy', 'true');

    fetch(url, { signal: signal })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var newMain = doc.getElementById(MAIN_ID);
        if (!newMain) throw new Error('No #' + MAIN_ID + ' in response');

        if (push) history.pushState({ variantSwitch: true }, '', url);
        if (doc.title) document.title = doc.title;

        // Scroll-reveal content (`.scroll-trigger.animate--*`) starts at
        // opacity:0 and is only shown by an IntersectionObserver bound at page
        // load — swapped-in nodes are never observed, so they'd stay invisible
        // (e.g. the gallery image). Cancel the animation on the new markup, the
        // same way Dawn does on its own AJAX swaps (see product-info.js).
        newMain.querySelectorAll('.scroll-trigger').forEach(function (element) {
          element.classList.add('scroll-trigger--cancel');
        });

        // The media zoom modal (and product popup modals) portal themselves out
        // to <body> on connect (ModalDialog.connectedCallback), so they live
        // OUTSIDE #MainContent and survive the swap — leaving a stale modal with
        // the previous product's images (and an id clash with the new one).
        // Remove them first; the swapped-in markup re-creates fresh ones.
        document
          .querySelectorAll('product-modal, modal-dialog[id^="PopupModal-"]')
          .forEach(function (modal) {
            modal.remove();
          });

        // Keep the shopper where they are — a variant switch shouldn't move the
        // viewport. Captured right before the swap and restored right after.
        var scrollX = window.scrollX;
        var scrollY = window.scrollY;

        swapInnerHTML(main, newMain.innerHTML);

        main.classList.remove(LOADING_CLASS);
        main.removeAttribute('aria-busy');

        if (focusWasInVariants) {
          var activeLink = main.querySelector(
            '.product__variants-wrapper a.active'
          );
          // preventScroll: focusing must not scroll the element into view.
          if (activeLink) activeLink.focus({ preventScroll: true });
        }

        window.scrollTo(scrollX, scrollY);

        refreshThirdPartyWidgets();
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        // Fall back to a normal navigation so the link always works.
        window.location.href = url;
      });
  }

  document.addEventListener('click', function (event) {
    // "Show more colours" toggle — delegated so it survives content swaps.
    var showMore = event.target.closest(SHOW_MORE_SELECTOR);
    if (showMore) {
      var list = showMore.closest('ul');
      if (list) list.classList.toggle('product__color-variations-list--show-all');
      return;
    }

    var link = event.target.closest(VARIANT_LINK_SELECTOR);
    if (!link) return;

    var main = getMain();
    if (!main || !main.contains(link)) return;
    if (isModifiedClick(event)) return;

    var target = new URL(link.href, window.location.origin);
    // Same page — nothing to switch to.
    if (target.pathname === window.location.pathname) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    navigate(link.href);
  });

  // Back/forward: re-render main for whatever URL we've landed on.
  window.addEventListener('popstate', function () {
    navigate(window.location.href, { push: false });
  });

  // Mark the initial entry so history state is consistent across swaps.
  if (!history.state) {
    history.replaceState({ variantSwitch: 'initial' }, '');
  }
})();
